import { Entity, Feature } from '@wayfinder/vms-base';
import * as R from 'ramda';
import CanonicalLoader from '../canonical-loader/canonical-loader';
import { actions as A, LOADING_STATUS_LOADED, LOADING_STATUS_LOADING } from './constants';
import { selectors } from './selectors';

/*
 * Construct a FUNCTION that takes arguments to use as an Action Creator
 * @sig def :: [String] -> ([*] -> { type: String, String: * })
 *
 * So:                     def(ADD_FEATURES, 'features', 'ordinal', 'levelId')
 * Returns this function:  (features, ordinal, levelId) => ({ type: ADD_FEATURES, features, ordinal, levelId })
 *
 */
const def =
    (type, ...argNames) =>
    (...args) =>
        R.mergeRight({ type }, R.zipObj(argNames, args));

const setLoadingStatusAction = def(A.SET_LOADING_STATUS, 'path', 'status');
const setVenueAction = def(A.SET_VENUE, 'venueId', 'venue');
const setOrdinalsAction = def(A.SET_ORDINALS, 'venueId', 'ordinals');
const setStructuresAction = def(A.SET_STRUCTURES, 'venueId', 'structures');
const setLevelsAction = def(A.SET_LEVELS, 'venueId', 'levels');
const setThemeAction = def(A.SET_THEME, 'venueId', 'theme');
const setVenueSummariesAction = def(A.SET_VENUE_SUMMARIES, 'venueSummaries');
const setMapStyleAction = def(A.SET_MAP_STYLE, 'venueId', 'mapStyle');
const setMapSpritesAction = def(A.SET_SPRITES, 'venueId', 'sprites');
const setIsMigratedAction = def(A.SET_IS_MIGRATED, 'venueId', 'isMigrated');

const setVenueDropdowns = def(A.SET_VENUE_DROPDOWNS, 'venueSummaries');
const setBoundingBoxAction = def(A.SET_BASEMAP_BOUNDING_BOX, 'venueId', 'features');

const addFeaturesAction = def(A.ADD_FEATURES, 'venueId', 'features');
const upsertEntitiesAction = def(A.UPSERT_ENTITIES, 'venueId', 'entities');

const removeFeatureAction = def(A.REMOVE_FEATURE, 'feature');
const resetAction = def(A.RESET);

const venueLoadingStarted = def(A.VENUE_LOADING_STARTED, 'venueId');
const venueLoadingFinished = def(A.VENUE_LOADING_FINISHED, 'venueId');
const venueLoadingFailed = def(A.VENUE_LOADING_FAILED, 'venueId', 'error');
const mapLoadingStarted = def(A.MAP_LOADING_STARTED, 'venueId');
const mapLoadingFinished = def(A.MAP_LOADING_FINISHED, 'venueId');
const mapLoadingFailed = def(A.MAP_LOADING_FAILED, 'venueId', 'error');

const performValidation = def(A.PERFORM_VALIDATION, 'venueId');

/*
 * Reset the state back to its initial values
 * @sig reset :: () -> ()
 */
const reset = resetAction;

// ---------------------------------------------------------------------------------------------------------------------
//
// Load
//
// ---------------------------------------------------------------------------------------------------------------------

/*
 * The actual data we're downloading does *NOT* go with the loading status. Eg.: if you're downloading  features,
 * those features will go in the 'features' key, not the 'loadingStatus' key (and the feature ids mights also be
 * added to the venue, structure, ordinal and level).
 *
 * We don't know how to download anything and we don't know how to access the result, so we use the two helper "thunks."
 *
 * accessFunction will be called to retrieve the "real" data from the state.
 *
 */
const downloadAndStore =
    (venueId, selector, loadingPath, loadFunction, actionCreator) => async (dispatch, getState) => {
        dispatch(setLoadingStatusAction(loadingPath, LOADING_STATUS_LOADING));
        const result = await loadFunction(venueId);
        dispatch(actionCreator(venueId, result));
        dispatch(setLoadingStatusAction(loadingPath, LOADING_STATUS_LOADED));
        return selector(venueId, getState());
    };

/*
 * Load the theme if necessary
 * Dispatches
 * @sig loadTheme :: String -> Promise Theme
 */
const loadTheme = (venueCategory, venueId) => async dispatch =>
    dispatch(
        downloadAndStore(
            venueId,
            selectors.getTheme,
            ['themes', venueId],
            CanonicalLoader.getTheme(venueCategory),
            setThemeAction,
        ),
    );

/*
 * Load the Venue if necessary
 * Impure changes the state
 * @sig loadVenue :: VenueId -> Promise Venue
 */
const loadVenue = venueId => async dispatch =>
    dispatch(
        downloadAndStore(venueId, selectors.getVenue, ['venues', venueId], CanonicalLoader.getVenue, setVenueAction),
    );

/*
 * Load the Ordinals for the Venue
 * Impure changes the state
 * @sig loadOrdinalsForVenue :: VenueId -> Promise [Ordinal]
 */
const loadOrdinalsForVenue = venueId => async dispatch =>
    dispatch(
        downloadAndStore(
            venueId,
            selectors.getOrdinals,
            ['ordinals', venueId],
            CanonicalLoader.getOrdinalsForVenue,
            setOrdinalsAction,
        ),
    );

/*
 * Load the Structures for the Venue
 * Impure changes the state
 * @sig loadStructuresForVenue :: VenueId -> Promise [Ordinal]
 */
const loadStructuresForVenue = venueId => async dispatch =>
    dispatch(
        downloadAndStore(
            venueId,
            selectors.getStructures,
            ['structures', venueId],
            CanonicalLoader.getStructuresForVenue,
            setStructuresAction,
        ),
    );

/*
 * Load the Levels for the Venue
 * Impure changes the state
 * @sig loadLevelsForVenue :: VenueId -> Promise [Ordinal]
 */
const loadLevelsForVenue = venueId => async dispatch =>
    dispatch(
        downloadAndStore(
            venueId,
            selectors.getLevels,
            ['levels', venueId],
            CanonicalLoader.getLevelsForVenue,
            setLevelsAction,
        ),
    );

const loadMapStyle = (venueCategory, venueId) => async dispatch =>
    dispatch(
        downloadAndStore(
            venueId,
            selectors.getMapStyle,
            ['mapStyle', venueId],
            CanonicalLoader.loadMapStyle(venueCategory),
            setMapStyleAction,
        ),
    );

const loadSprites = (venueCategory, venueId) => async dispatch =>
    dispatch(
        downloadAndStore(
            venueId,
            selectors.getSprites,
            ['sprites', venueId],
            CanonicalLoader.getSpritesForValidation(venueCategory),
            setMapSpritesAction,
        ),
    );

/*
 * Load the Features for the venue's background if necessary
 * Impure changes the state
 * @sig loadFeaturesForVenueBackground :: VenueId -> Promise ()
 */
const loadFeaturesForVenueBackground = (venueId, theme) => async (dispatch, getState) => {
    const loadingPath = ['features', venueId];

    dispatch(setLoadingStatusAction(loadingPath, LOADING_STATUS_LOADING));
    const result = await CanonicalLoader.getFeaturesForVenueBackground(theme)(venueId);
    dispatch(setBoundingBoxAction(venueId, result));
    dispatch(addFeaturesAction(venueId, result));
    dispatch(setLoadingStatusAction(loadingPath, LOADING_STATUS_LOADED));
    return selectors.featuresForBackground(venueId, getState());
};

// load all levels of the venueId in parallel
const loadFeaturesForAllOrdinals =
    (venueId, levels, theme, entities, isFeatureMigrated) => async (dispatch, getState) => {
        try {
            const loadFeaturesForOrdinal = async ordinalId => {
                const features = await CanonicalLoader.getFeaturesForOrdinal(
                    venueId,
                    ordinalId,
                    levels,
                    theme,
                    entities,
                    isFeatureMigrated,
                );
                dispatch(addFeaturesAction(venueId, features));
            };

            dispatch(mapLoadingStarted(venueId));

            const defaultOrdinalId = R.prop('id', selectors.defaultOrdinalForVenue(venueId, getState()));
            const ordinalIds = R.without([defaultOrdinalId], selectors.ordinalIdsForVenue(venueId, getState()));

            // load the default ordinal first so it shows up on the map faster
            await loadFeaturesForOrdinal(defaultOrdinalId);
            await Promise.all(R.map(loadFeaturesForOrdinal, ordinalIds));

            dispatch(mapLoadingFinished(venueId));
        } catch (e) {
            dispatch(mapLoadingFailed(venueId, e));
        }
    };

/*
 * Load the Entities for the Venue if necessary
 * Impure: changes the state
 * @sig loadEntities :: VenueId -> Promise [Entity]
 */
const loadEntities = (venueId, isEntityMigrated, locale, baseVenueId) => async dispatch =>
    dispatch(
        downloadAndStore(
            venueId,
            selectors.entitiesForVenue,
            ['entities', venueId],
            CanonicalLoader.getEntities(isEntityMigrated, locale, baseVenueId),
            upsertEntitiesAction,
        ),
    );

/*
 * Load venue summaries and enrich them for VMS use.
 * Dispatches
 */
const loadVenueSummaries = () => async dispatch => {
    dispatch(setLoadingStatusAction(['venueSummaries'], LOADING_STATUS_LOADING));
    const venueSummaries = await CanonicalLoader.getVenueSummaries();
    dispatch(setVenueSummariesAction(venueSummaries));
    dispatch(setLoadingStatusAction(['venueSummaries'], LOADING_STATUS_LOADED));
    return venueSummaries;
};

const loadIsMigrated = venueId => async dispatch =>
    dispatch(
        downloadAndStore(
            venueId,
            selectors.getIsEntityMigrated,
            ['isMigrated', venueId],
            CanonicalLoader.getIsMigrated,
            setIsMigratedAction,
        ),
    );

/*
 * See the discussion at fakeEnrichVenueSummaryData for why we need both venueId and testVenueId
 * @returns success
 * @sig startVenue :: (VenueId, String) -> DispatchFunction -> Boolean
 */
const startVenue =
    (venueId, locale = '', baseVenueId) =>
    async (dispatch, getState) => {
        try {
            CanonicalLoader.setFilePaths(venueId);
            dispatch(venueLoadingStarted(venueId));
            await dispatch(loadIsMigrated(venueId));
            const isEntityMigrated = selectors.getIsEntityMigrated(venueId, getState());
            const isFeatureMigrated = selectors.getIsFeatureMigrated(venueId, getState());
            const [venue, , , levels, entities] = await Promise.all([
                dispatch(loadVenue(venueId)),
                dispatch(loadOrdinalsForVenue(venueId)),
                dispatch(loadStructuresForVenue(venueId)),
                dispatch(loadLevelsForVenue(venueId)),
                dispatch(loadEntities(venueId, isEntityMigrated, locale, baseVenueId)),
            ]);

            // theme needs the metadata, features need the theme
            const theme = await dispatch(loadTheme(venue.venueCategory, venueId));

            // load *ALL* the levels so we can use the results to enrich the Entities
            // TODO: don't load all the levels just so zoomToEntity will work!
            if (venue && R.propEq('v5', 'assetFormatVersion', venue)) {
                Promise.all([
                    dispatch(loadFeaturesForVenueBackground(venueId, theme)),
                    dispatch(loadFeaturesForAllOrdinals(venueId, levels, theme, entities, isFeatureMigrated)),
                    dispatch(loadMapStyle(venue.venueCategory, venueId)),
                    dispatch(loadSprites(venue.venueCategory, venueId)),
                ]).then(() => {
                    dispatch(venueLoadingFinished(venueId));
                    dispatch(performValidation(venueId));
                });
            } else {
                dispatch(venueLoadingFinished(venueId));
                dispatch(performValidation(venueId));
            }

            return true;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            dispatch(venueLoadingFailed(venueId, e));
            return false;
        }
    };

// ---------------------------------------------------------------------------------------------------------------------
//
// Update Feature
//
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Add an individual Feature to the state
 * Dispatches
 * @sig addFeature :: (Feature, LevelId) -> Feature
 * Note: a featureId will be constructed if none is provided
 */
const addFeature = (feature, levelId) => (dispatch, getState) => {
    const featureId = R.path(['properties', 'subid'], feature) || _createFeatureId(feature);
    const venueId = selectors.venueIdForLevel(levelId, getState());

    feature = R.mergeDeepRight(feature, {
        id: featureId,
        $loki: undefined,
        properties: {
            subid: featureId,
            levelId,
            venueId,
            structureId: selectors.structureIdForLevel(levelId, getState()),
            ordinalId: selectors.ordinalIdForLevel(levelId, getState()),
        },
    });

    dispatch(addFeaturesAction(venueId, [feature]));
    return feature;
};

/*
 * Update a Feature with a new one passed in (that is, remove any old feature and then add the new one)
 * Dispatches
 * @sig updateFeature :: Feature -> Feature
 */
const updateFeature = (feature, levelId) => dispatch => {
    dispatch(removeFeature(feature.properties.subid));
    return dispatch(addFeature(feature, levelId || feature.properties.levelId));
};

/*
 * Remove the feature from the state along with all its connections (and return the Feature)
 * Dispatches
 * @sig removeFeature :: Feature|FeatureId => ()
 */
const removeFeature = featureOrFeatureId => dispatch => {
    const feature = R.is(String, featureOrFeatureId) ? selectors.getFeature(featureOrFeatureId) : featureOrFeatureId;

    // no such feature!
    if (feature) return dispatch(removeFeatureAction(feature));
};

// ---------------------------------------------------------------------------------------------------------------------
//
// Update Entity
//
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Remove the old feature
 * Update a polygon feature to have the id and category of the entity passed in
 * Add the 'new' feature
 * @sig openPolygon :: (Entity, Feature) -> ()
 */
const openPolygon = polygonFeature => (dispatch, getState) => {
    const venueId = polygonFeature.properties.venueId;
    const levelId = polygonFeature.properties.levelId;
    const latLng = Entity.guessLatLng([polygonFeature], true);
    const level = selectors.getLevel(levelId, getState());
    const structure = selectors.getStructureFromLevelId(levelId, getState());
    const ordinal = selectors.ordinalForLevel(levelId, getState());

    // (latLng, level, structure, includeBadge, showName)
    const entity = selectors.createIdForEntity(
        venueId,
        Entity.createDefaultEntity(latLng, level, structure, ordinal.ordinalNumber, false, true),
    );

    dispatch(
        addEntity({
            entity,
            showBadge: false,
            showName: true,
            latLng,
            levelId,
            featuresForEntity: [polygonFeature],
        }),
    );

    dispatch(removeFeature(polygonFeature));
    polygonFeature = Feature.updatePolygonFeatureForEntity(
        entity,
        polygonFeature,
        selectors.getTheme(venueId, getState()),
    );
    dispatch(addFeature(polygonFeature, polygonFeature.properties.levelId));
    return entity;
};

/*
 * Process the instructions embedded in instructions. Instructions are returned by functions in Entity
 */
const _processFeatureInstructions =
    ({ instructions, levelId }) =>
    dispatch => {
        R.map(f => dispatch(addFeature(f, levelId)), instructions.addFeatures || []);
        R.map(f => dispatch(removeFeature(f)), instructions.removeFeatures || []);
        R.map(f => dispatch(updateFeature(f, levelId)), instructions.updateFeatures || []);
    };

/*
 * Add a symbol Feature with details from the entity and other parameters. All parameters are required.
 * Impure: updates state
 * @sig addEntity :: ({ entity, showBadge, showName, levelId, latLng }) -> Entity
 */
const addEntity =
    ({ entity, showBadge, showName, levelId, latLng, featuresForEntity }) =>
    (dispatch, getState) => {
        const instructions = Entity.instructionsForAddEntity({
            theme: selectors.getTheme(entity.venueId, getState()),
            entity,
            showBadge,
            showName,
            latLng,
            featuresForEntity,
        });

        dispatch(_processFeatureInstructions({ levelId, instructions }));
        return entity;
    };

/*
 * Update the subfeatures associated with the given Entity. These are potentially:
 *
 * - a polygon subfeature
 * - a symbol subfeature (text and/or badge)
 *
 * The change can come either from the Entity itself, if the category or name changes
 * OR from the extra parameters showBadge, showName, levelId, latLng which are not themselves part of any Entity
 */
const updateEntity =
    ({ entity, showBadge, showName, latLng, levelId }) =>
    (dispatch, getState) => {
        const position = selectors.positionForEntity(entity.id, getState());
        latLng = latLng || position;
        const theme = selectors.getTheme(entity.venueId, getState());
        const instructions = Entity.instructionsForUpdateEntity({
            theme,
            entity,
            featuresForEntity: selectors.featuresForEntity(entity.id),
            showBadge,
            showName,
            latLng,
        });
        dispatch(_processFeatureInstructions({ levelId: levelId || position.levelId, instructions }));
        return entity;
    };

/*
 * Remove all the Feature's related to the Entity specified by entityId -- EXCEPT the Polygon
 * if the boolean is true, remove the polygon
 * Impure: modifies state
 * @sig removeAiEntity :: (EntityId, Boolean) -> ()
 */
const removeEntity = (entityId, removePolygon) => dispatch => {
    const instructions = Entity.instructionsForRemoveEntity(selectors.featuresForEntity(entityId), removePolygon);
    dispatch(_processFeatureInstructions({ instructions }));
};

/*
 * Create a FeatureId by appending a letter to the Feature's EntityId.
 * More specifically, the letter is one more than the previously-existing subid (aka FeatureId at the moment)
 * Impure: depends on data in state
 */
const _createFeatureId = feature => {
    const otherFeatureIdsForEntity = selectors.featureIdsForEntity(R.path(['properties', 'id'], feature));

    if (otherFeatureIdsForEntity.length === 0) {
        // there are NO features with subids yet, so just append 'a'
        return `${feature.properties.id}a`;
    }

    // find the subid with the highest suffix and add 1 to that (eg. if 14c exists return 14d)
    const lastExistingSubid = R.last(otherFeatureIdsForEntity.sort());
    const [prefix, suffix] = [R.dropLast(1, lastExistingSubid), R.takeLast(1, lastExistingSubid)];
    return prefix + String.fromCharCode(suffix.charCodeAt(0) + 1);
};

// testing
const _forTesting = {
    loadTheme,
    loadFeaturesForVenueBackground,
    loadVenue,
    loadOrdinalsForVenue,
    loadStructuresForVenue,
    loadLevelsForVenue,
    loadFeaturesForAllOrdinals,
};

export {
    startVenue,
    loadVenueSummaries,
    loadEntities,
    _forTesting,
    upsertEntitiesAction,
    addFeature,
    updateFeature,
    removeFeature,
    addEntity,
    updateEntity,
    removeEntity,
    openPolygon,
    reset,
    venueLoadingFailed,
    venueLoadingFinished,
    setVenueDropdowns,
    setMapStyleAction,
    setThemeAction,
    addFeaturesAction,
};
